export interface MemberColor {
  name: string;
  text: string;
  bg: string;
  border: string;
  avatar: string;
  badge: string;
  bubbleStyle: {
    backgroundColor: string;
    borderColor: string;
  };
}

export const MEMBER_PALETTE: MemberColor[] = [
  {
    name: 'emerald',
    text: 'text-emerald-400',
    bg: 'bg-emerald-950/60',
    border: 'border-emerald-500/40',
    avatar: 'bg-emerald-500/20 text-emerald-300',
    badge: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
    bubbleStyle: {
      backgroundColor: 'rgba(6, 78, 59, 0.22)',
      borderColor: 'rgba(16, 185, 129, 0.35)',
    },
  },
  {
    name: 'amber',
    text: 'text-amber-400',
    bg: 'bg-amber-950/60',
    border: 'border-amber-500/40',
    avatar: 'bg-amber-500/20 text-amber-300',
    badge: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    bubbleStyle: {
      backgroundColor: 'rgba(120, 53, 15, 0.22)',
      borderColor: 'rgba(245, 158, 11, 0.35)',
    },
  },
  {
    name: 'purple',
    text: 'text-purple-400',
    bg: 'bg-purple-950/60',
    border: 'border-purple-500/40',
    avatar: 'bg-purple-500/20 text-purple-300',
    badge: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
    bubbleStyle: {
      backgroundColor: 'rgba(88, 28, 135, 0.22)',
      borderColor: 'rgba(168, 85, 247, 0.35)',
    },
  },
  {
    name: 'pink',
    text: 'text-pink-400',
    bg: 'bg-pink-950/60',
    border: 'border-pink-500/40',
    avatar: 'bg-pink-500/20 text-pink-300',
    badge: 'bg-pink-500/20 text-pink-300 border-pink-500/30',
    bubbleStyle: {
      backgroundColor: 'rgba(131, 24, 67, 0.22)',
      borderColor: 'rgba(236, 72, 153, 0.35)',
    },
  },
  {
    name: 'cyan',
    text: 'text-cyan-400',
    bg: 'bg-cyan-950/60',
    border: 'border-cyan-500/40',
    avatar: 'bg-cyan-500/20 text-cyan-300',
    badge: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
    bubbleStyle: {
      backgroundColor: 'rgba(22, 78, 99, 0.22)',
      borderColor: 'rgba(6, 182, 212, 0.35)',
    },
  },
  {
    name: 'orange',
    text: 'text-orange-400',
    bg: 'bg-orange-950/60',
    border: 'border-orange-500/40',
    avatar: 'bg-orange-500/20 text-orange-300',
    badge: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
    bubbleStyle: {
      backgroundColor: 'rgba(124, 45, 18, 0.22)',
      borderColor: 'rgba(249, 115, 22, 0.35)',
    },
  },
  {
    name: 'indigo',
    text: 'text-indigo-400',
    bg: 'bg-indigo-950/60',
    border: 'border-indigo-500/40',
    avatar: 'bg-indigo-500/20 text-indigo-300',
    badge: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30',
    bubbleStyle: {
      backgroundColor: 'rgba(49, 46, 129, 0.22)',
      borderColor: 'rgba(99, 102, 241, 0.35)',
    },
  },
  {
    name: 'teal',
    text: 'text-teal-400',
    bg: 'bg-teal-950/60',
    border: 'border-teal-500/40',
    avatar: 'bg-teal-500/20 text-teal-300',
    badge: 'bg-teal-500/20 text-teal-300 border-teal-500/30',
    bubbleStyle: {
      backgroundColor: 'rgba(19, 78, 74, 0.22)',
      borderColor: 'rgba(20, 184, 166, 0.35)',
    },
  },
  {
    name: 'rose',
    text: 'text-rose-400',
    bg: 'bg-rose-950/60',
    border: 'border-rose-500/40',
    avatar: 'bg-rose-500/20 text-rose-300',
    badge: 'bg-rose-500/20 text-rose-300 border-rose-500/30',
    bubbleStyle: {
      backgroundColor: 'rgba(136, 19, 55, 0.22)',
      borderColor: 'rgba(244, 63, 94, 0.35)',
    },
  },
  {
    name: 'violet',
    text: 'text-violet-400',
    bg: 'bg-violet-950/60',
    border: 'border-violet-500/40',
    avatar: 'bg-violet-500/20 text-violet-300',
    badge: 'bg-violet-500/20 text-violet-300 border-violet-500/30',
    bubbleStyle: {
      backgroundColor: 'rgba(76, 29, 149, 0.22)',
      borderColor: 'rgba(139, 92, 246, 0.35)',
    },
  },
];

const contextColorMaps: Map<string, Map<string, MemberColor>> = new Map();

/**
 * Retrieves a deterministic, non-duplicating member color.
 * Uses hash + linear probing collision resolution per context (conversation)
 * to ensure that distinct members do not share colors.
 */
export function getMemberColor(key: string, contextId?: string): MemberColor {
  const normKey = (key || 'unknown').trim().toLowerCase();
  const ctxKey = contextId ? contextId.trim() : 'global';

  if (!contextColorMaps.has(ctxKey)) {
    contextColorMaps.set(ctxKey, new Map());
  }

  const memberMap = contextColorMaps.get(ctxKey)!;

  if (memberMap.has(normKey)) {
    return memberMap.get(normKey)!;
  }

  // Calculate preferred initial hash index
  let hash = 0;
  for (let i = 0; i < normKey.length; i++) {
    hash = normKey.charCodeAt(i) + ((hash << 5) - hash);
  }
  const preferredIndex = Math.abs(hash) % MEMBER_PALETTE.length;

  // Track colors already assigned in this context
  const usedIndices = new Set<number>();
  for (const assigned of memberMap.values()) {
    const idx = MEMBER_PALETTE.findIndex(c => c.name === assigned.name);
    if (idx !== -1) usedIndices.add(idx);
  }

  let assignedIndex = preferredIndex;

  // If collision occurs (color already assigned to another member in this context),
  // probe for the next unused color in the palette
  if (usedIndices.has(preferredIndex) && usedIndices.size < MEMBER_PALETTE.length) {
    for (let offset = 1; offset < MEMBER_PALETTE.length; offset++) {
      const candidateIndex = (preferredIndex + offset) % MEMBER_PALETTE.length;
      if (!usedIndices.has(candidateIndex)) {
        assignedIndex = candidateIndex;
        break;
      }
    }
  }

  const assignedColor = MEMBER_PALETTE[assignedIndex];
  memberMap.set(normKey, assignedColor);
  return assignedColor;
}

/**
 * Resets the assigned color cache for a specific context or completely.
 */
export function clearMemberColorCache(contextId?: string) {
  if (contextId) {
    contextColorMaps.delete(contextId.trim());
  } else {
    contextColorMaps.clear();
  }
}
